import { isIPv4InCIDR } from "modules/databases/loadBalancers";
import { Badge } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { DatabasePasswordDialog } from "components/common/DatabasePasswordDialog";
import { FormDialog, selectOptionSchema } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps,
  SelectOption
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { Table } from "components/common/Table";
import {
  TABLE_SORTING_TYPES,
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import {
  availableExtensions,
  extensionDetails,
  extensionHost,
  extensionOptions,
  selectedExtensionNames
} from "modules/databases/extensions";
import { databasesActions } from "modules/databases/slices";
import * as databasesSelectors from "modules/databases/selectors";
import {
  DATABASES_DEFAULT_PORTS,
  DATABASES_LABELS,
  DBServicesParamsSelector,
  createVersionOptions,
  databaseSelector,
  extensionCataloguesSelector,
  isDatabaseCreatingSelector,
  isDatabaseDeletingSelector,
  isDatabaseStatusChangingSelector,
  isDatabaseUpdatingSelector,
  supportsExtensions,
  tableDatabasesSelector,
  versionSelectOptions
} from "modules/databases/selectors";
import {
  CHANGE_STATUS_TYPES,
  DATABASES,
  DATABASE_STATUSES,
  TableDatabase
} from "modules/databases/types";
import {
  diskSizeSchema,
  instanceNameSchema,
  instanceUserSchema,
  ipACLSchema
} from "modules/databases/validation";
import { enterprisesActions } from "modules/enterprises/slices";
import { organizationSelector } from "modules/enterprises/selectors";
import { networksActions } from "modules/networks/slices";
import {
  subnetsSelector,
  tableNetworksSelector
} from "modules/networks/selectors";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { projectsActions } from "modules/projects/slices";
import { projectSelector } from "modules/projects/selectors";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useParams } from "react-router-dom";
import { isSelectOption } from "typeGuards/isSelectOption";
import { isString } from "typeGuards/isString";
import { isUndefined } from "typeGuards/isUndefined";
import { getSelectDefaultValue } from "utils/getSelectDefaultValue";
import { getSelectOption } from "utils/getSelectOption";
import { splitMultilineCommaSeparatedList } from "utils/splitMultilineCommaSeparatedList";
import { Schema, StringSchema, array, mixed, number, string } from "yup";
import { MAX_PORT, MIN_PORT, REGEX, ROUTES } from "../../constants";
import { DIALOG_TYPES } from "./types";

const POLL_ID_PREFIX = "DATABASES";

const POLL_IDS = {
  databases: "DATABASES",
  networks: "NETWORKS",
  subnets: "SUBNETS"
};

const tableColumns: TableColumn<TableDatabase>[] = [
  { key: "name", label: "Name" },
  { key: "service_status", label: "Status" },
  { key: "mode", label: "Mode" },
  { key: "connectionString", label: "Connection string" },
  { key: "versionString", label: "Version" },
  { key: "cpuString", label: "vCPUs", sortingType: TABLE_SORTING_TYPES.NUMBER },
  {
    key: "memoryString",
    label: "Memory",
    sortingType: TABLE_SORTING_TYPES.NUMBER
  },
  { key: "diskString", label: "Disk", sortingType: TABLE_SORTING_TYPES.NUMBER },
  { key: "ageString", label: "Created" }
];

const title = "Databases";

const EXTENSIONS_NOTE = "ℹ️ Created in the instance's initial database.";

const databasePasswordTextAfterCreation =
  "Database has been successfully created. Please save the password for further use.";

export const Databases: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    organizationId: string;
    regionId: string;
    projectId: string;
  }>();
  const organization = useSelector(organizationSelector);
  const project = useSelector(projectSelector);
  const database = useSelector(databaseSelector);
  const subnets = useSelector(subnetsSelector);
  const networks = useSelector(tableNetworksSelector);
  const DBParams = useSelector(DBServicesParamsSelector);
  const extensionCatalogues = useSelector(extensionCataloguesSelector);
  const [isDatabasePasswordDialogOpened, setIsDatabasePasswordDialogOpened] =
    useState(false);
  const databases = useSelector(tableDatabasesSelector);
  const createdDatabasePassword = useSelector(
    databasesSelectors.createdDatabasePasswordSelector
  );
  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.CREATE, isOpened: false });
  const [
    selectedDatabasePasswordDialogText,
    setSelectedDatabasePasswordDialogText
  ] = useState<string>(databasePasswordTextAfterCreation);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const isDatabaseCreating = useSelector(isDatabaseCreatingSelector);
  const isDatabaseUpdating = useSelector(isDatabaseUpdatingSelector);
  const isDatabaseDeleting = useSelector(isDatabaseDeletingSelector);
  const isDatabaseStatusChanging = useSelector(
    isDatabaseStatusChangingSelector
  );

  const isOperationInProgress =
    isDatabaseCreating ||
    isDatabaseUpdating ||
    isDatabaseDeleting ||
    isDatabaseStatusChanging;
  const previousIsOperationInProgress = usePrevious(isOperationInProgress);

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
    setSelectedItemId(null);
  }, [dialog]);

  const generateTableItemURL = useCallback(
    (id: string) => {
      const databaseServiceName = databases?.find(
        (database) => database.id === id
      )?.serviceName;

      if (databaseServiceName) {
        return generatePath(ROUTES.DATABASE, {
          organizationId: matchParams.organizationId,
          regionId: matchParams.regionId,
          projectId: matchParams.projectId,
          serviceName: databaseServiceName,
          databaseId: id
        });
      }
    },
    [
      databases,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.regionId
    ]
  );

  const breadcrumbs: Breadcrumb[] = [
    { text: "Organizations", url: ROUTES.ORGANIZATIONS },
    {
      text: organization?.name || "",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Projects",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: project?.name || "",
      url: generatePath(ROUTES.PROJECT, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    },
    {
      text: "Databases",
      url: generatePath(ROUTES.DATABASES, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    }
  ];

  const handleCreateDatabaseButtonClick = useCallback(() => {
    setSelectedDatabasePasswordDialogText(databasePasswordTextAfterCreation);
    setDialog({
      type: DIALOG_TYPES.CREATE,
      isOpened: true
    });
  }, []);

  const handleEditDatabaseMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.EDIT,
      isOpened: true
    });
  }, []);

  const handleDeleteDatabaseMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE,
      isOpened: true
    });
  }, []);

  const handleChangeDatabaseStatusButtonClick = useCallback(
    (id: string) => {
      const serviceName = databases?.find(
        (database) => database.id === id
      )?.serviceName;

      if (serviceName) {
        dispatch(
          databasesActions.changeDatabaseStatusRequest({
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            id: id,
            serviceName,
            type: CHANGE_STATUS_TYPES.START
          })
        );
      }
    },
    [databases, dispatch, matchParams.organizationId, matchParams.projectId]
  );

  const tableActions: TableRowActionsMenuItem<TableDatabase>[] = [
    {
      label: "Start",
      handler: handleChangeDatabaseStatusButtonClick,
      isDisabled: (database) =>
        database.service_status !== DATABASE_STATUSES.STOPPED
    },
    {
      label: "Edit",
      handler: handleEditDatabaseMenuItemClick
    },
    {
      label: "Delete",
      handler: handleDeleteDatabaseMenuItemClick
    }
  ];

  useMount(() => {
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.databases}`,
        actionCreator: databasesActions.getDatabasesRequest,
        params: {
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!
        }
      })
    );
    dispatch(
      enterprisesActions.getOrganizationRequest({
        id: matchParams.organizationId!
      })
    );
    dispatch(
      projectsActions.getProjectRequest({
        regionId: matchParams.regionId!,
        id: matchParams.projectId!
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.networks}`,
        actionCreator: networksActions.getNetworksRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.subnets}`,
        actionCreator: networksActions.getSubnetsRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        }
      })
    );
    dispatch(databasesActions.getDBServicesParamsRequest({}));
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(projectsActions.clear());
    dispatch(databasesActions.clear());
    dispatch(networksActions.clear());
  });

  useEffect(() => {
    if (previousIsOperationInProgress && !isOperationInProgress) {
      dispatch(
        databasesActions.getDatabasesRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!
        })
      );
    }
  }, [
    previousIsOperationInProgress,
    isOperationInProgress,
    dispatch,
    matchParams.organizationId,
    matchParams.projectId
  ]);

  useEffect(() => {
    if (!isDatabaseCreating && createdDatabasePassword) {
      setSelectedDatabasePasswordDialogText(
        `Please save the password for further use:`
      );
      setIsDatabasePasswordDialogOpened(true);
      dispatch(databasesActions.clearCreatedDatabasePassword());
    }
  }, [isDatabaseCreating, createdDatabasePassword, dispatch]);

  const handleConfirmCreateDatabase = useCallback(
    (data: {
      name: string;
      user: string;
      ipACL: string;
      cpu: SelectOption;
      memory: SelectOption;
      disk: string;
      port: string;
      version: SelectOption | null;
      serviceName: SelectOption;
      enable_ilb: boolean;
      network_id: SelectOption | null;
      subnet_id: SelectOption | null;
      private_static_ip: string;
      ha_enabled: boolean;
      extensions?: SelectOption[];
    }) => {
      dispatch(
        databasesActions.createDatabaseRequest({
          orgId: matchParams.organizationId!,
          projId: matchParams.projectId!,
          serviceName: data.serviceName.value,
          data: {
            name: data.name,
            ...(data.user ? { user: data.user } : {}),
            ...(data.version?.value ? { version: data.version.value } : {}),
            cpu: Number(data.cpu.value),
            memory: Number(data.memory.value),
            ...(data.port ? { port: Number(data.port) } : {}),
            disk: Number(data.disk),
            ...(data.ipACL
              ? {
                  ip_acl: splitMultilineCommaSeparatedList(data.ipACL)
                }
              : {}),
            ...(data.subnet_id ? { subnet_id: data.subnet_id.value } : {}),
            ...(data.private_static_ip
              ? { private_static_ip: data.private_static_ip }
              : {}),
            ...(data.ha_enabled
              ? { high_availability: { enabled: true } }
              : {}),
            ...(data.extensions?.length
              ? { extensions: data.extensions.map((x) => x.value) }
              : {})
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      matchParams.projectId,
      handleCloseDialog,
      matchParams.organizationId
    ]
  );

  const handleConfirmEditDatabase = useCallback(
    (data: {
      cpu: SelectOption;
      memory: SelectOption;
      port: string;
      version: SelectOption;
      ha_enabled: boolean;
    }) => {
      if (selectedItemId) {
        const edited = databases?.find(
          (database) => database.id === selectedItemId
        );
        const serviceName = edited?.serviceName;
        const hadHA = Boolean(edited?.high_availability?.enabled);

        if (serviceName) {
          dispatch(
            databasesActions.updateDatabaseRequest({
              orgId: matchParams.organizationId!,
              projId: matchParams.projectId!,
              serviceName,
              id: selectedItemId,
              data: {
                // Compared with the SPEC version: re-sending the running one
                // mid-upgrade reverts spec.version, which cancels the upgrade.
                ...(data.version.value !== edited?.version
                  ? { version: data.version.value }
                  : {}),
                cpu: Number(data.cpu.value),
                memory: Number(data.memory.value),
                ...(Number(data.port) > 0 && Number(data.port) !== edited?.port
                  ? { port: Number(data.port) }
                  : {}),
                ...(data.ha_enabled !== hadHA
                  ? { high_availability: { enabled: data.ha_enabled } }
                  : {})
              }
            })
          );
        }
      }
      handleCloseDialog();
    },
    [
      selectedItemId,
      handleCloseDialog,
      dispatch,
      matchParams.organizationId,
      matchParams.projectId,
      databases
    ]
  );

  const handleConfirmDeleteDatabase = useCallback(() => {
    if (selectedItemId) {
      const serviceName = databases?.find(
        (database) => database.id === selectedItemId
      )?.serviceName;

      if (serviceName) {
        dispatch(
          databasesActions.deleteDatabaseRequest({
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!,
            serviceName,
            id: selectedItemId
          })
        );
      }

      handleCloseDialog();
    }
  }, [
    selectedItemId,
    dispatch,
    matchParams.organizationId,
    matchParams.projectId,
    databases,
    handleCloseDialog
  ]);
  const handleConfirmDatabasePasswordDialog = useCallback(() => {
    setIsDatabasePasswordDialogOpened(false);
  }, []);

  const previousSelectedItemId = usePrevious(selectedItemId);
  const deletingItemId = selectedItemId
    ? selectedItemId
    : previousSelectedItemId;
  const deletingDatabaseName = databases?.find(
    (database) => database.id === deletingItemId
  )?.name;

  const editedDatabase = databases?.find(
    (database) => database.id === selectedItemId
  );

  const offeredExtensionsFor = (fieldValues: Record<string, unknown>) => {
    const serviceName = fieldValues.serviceName;
    return isSelectOption(serviceName)
      ? extensionCatalogues[serviceName.value] || []
      : [];
  };

  // A new instance runs the chosen version, or the catalogue default (its first), on the
  // official image.
  const extensionHostFor = (
    fieldValues: Record<string, unknown>,
    chosenVersion: unknown = fieldValues.version
  ) => {
    const serviceName = fieldValues.serviceName;
    const defaultVersion = DBParams?.find(
      (params) =>
        isSelectOption(serviceName) && params.service_name === serviceName.value
    )?.versions[0];
    return extensionHost(
      isSelectOption(chosenVersion) ? chosenVersion.value : defaultVersion
    );
  };

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.CREATE]: {
      onConfirm: handleConfirmCreateDatabase,
      title: "Create database",
      confirmButtonLabel: "Create",
      fields: [
        {
          name: "serviceName",
          type: FIELD_TYPES.SELECT,
          label: "Service name",
          options: Object.keys(DATABASES_LABELS).map((x) => ({
            label: DATABASES_LABELS[x],
            value: x
          })),
          onChange: ({ value, setValue }) => {
            if (isSelectOption(value)) {
              setValue(
                "port",
                DATABASES_DEFAULT_PORTS[value.value as DATABASES]
              );
              setValue("extensions", []);
            }
          },
          rules: selectOptionSchema
        },
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          rules: instanceNameSchema
        },
        {
          name: "version",
          type: FIELD_TYPES.SELECT,
          label: "Version",
          options: (fieldValues) =>
            createVersionOptions(
              DBParams?.find(
                (params) =>
                  isSelectOption(fieldValues.serviceName) &&
                  params.service_name === fieldValues.serviceName.value
              )?.versions
            ),

          onChange: ({ value, setValue, fieldValues }) => {
            // A version can rule out an extension that was already picked.
            const installable = availableExtensions(
              offeredExtensionsFor(fieldValues),
              extensionHostFor(fieldValues, value)
            ).map((extension) => extension.name);
            setValue(
              "extensions",
              (Array.isArray(fieldValues.extensions)
                ? fieldValues.extensions
                : []
              ).filter(
                (option) =>
                  isSelectOption(option) && installable.includes(option.value)
              )
            );
          },
          rules: selectOptionSchema.nullable(),
          isHidden: (fieldValues) => !fieldValues.serviceName
        },
        {
          name: "extensions",
          type: FIELD_TYPES.MULTISELECT,
          label: "Extensions",
          options: (fieldValues) =>
            extensionOptions(offeredExtensionsFor(fieldValues), {
              host: extensionHostFor(fieldValues)
            }),
          rules: array(selectOptionSchema),
          isHidden: (fieldValues) =>
            !supportsExtensions(offeredExtensionsFor(fieldValues))
        },
        {
          name: "extensionsInfo",
          type: FIELD_TYPES.LABEL,
          label: (fieldValues) =>
            [
              EXTENSIONS_NOTE,
              extensionDetails(
                offeredExtensionsFor(fieldValues),
                extensionHostFor(fieldValues),
                selectedExtensionNames(fieldValues.extensions)
              )
            ]
              .filter(Boolean)
              .join("\n"),
          isHidden: (fieldValues) =>
            !supportsExtensions(offeredExtensionsFor(fieldValues))
        },
        {
          name: "user",
          type: FIELD_TYPES.TEXT,
          label: "DB user",
          rules: instanceUserSchema
        },
        {
          name: "port",
          type: FIELD_TYPES.NUMBER,
          label: "Port",
          defaultValue: (fieldValues: Record<string, unknown>) =>
            isSelectOption(fieldValues.serviceName)
              ? Number(
                  DATABASES_DEFAULT_PORTS[
                    fieldValues.serviceName.value as DATABASES
                  ]
                )
              : undefined,
          min: MIN_PORT,
          max: MAX_PORT,
          rules: number()
            .integer()
            .min(
              MIN_PORT,
              `Port must be greater than or equals to ${MIN_PORT}.`
            )
            .max(MAX_PORT, `Port must be less than ${MAX_PORT}.`),
          isHidden: (fieldValues) => !fieldValues.serviceName
        },
        {
          name: "cpu",
          type: FIELD_TYPES.SELECT,
          label: "vCPUs",
          options: (fieldValues) =>
            DBParams?.find(
              (params) =>
                isSelectOption(fieldValues.serviceName) &&
                params.service_name === fieldValues.serviceName.value
            )?.cpu.map((x) => ({
              label: `${Number(x) / 1000} vCPU`,
              value: x
            })),
          rules: selectOptionSchema,
          isHidden: (fieldValues) => !fieldValues.serviceName
        },
        {
          name: "memory",
          type: FIELD_TYPES.SELECT,
          label: "Memory",
          options: (fieldValues) =>
            DBParams?.find(
              (params) =>
                isSelectOption(fieldValues.serviceName) &&
                params.service_name === fieldValues.serviceName.value
            )?.memory.map((x) => ({
              label: `${Number(x) / 1024} GiB`,
              value: x
            })),
          rules: selectOptionSchema,
          isHidden: (fieldValues) => !fieldValues.serviceName
        },
        {
          name: "disk",
          type: FIELD_TYPES.NUMBER,
          label: "Disk",
          suffix: "MiB",
          min: (fieldValues) =>
            DBParams?.find(
              (params) =>
                isSelectOption(fieldValues.serviceName) &&
                params.service_name === fieldValues.serviceName.value
            ) &&
            Number(
              DBParams?.find(
                (params) =>
                  isSelectOption(fieldValues.serviceName) &&
                  params.service_name === fieldValues.serviceName.value
              )?.disk_min
            ),
          max: (fieldValues) =>
            DBParams?.find(
              (params) =>
                isSelectOption(fieldValues.serviceName) &&
                params.service_name === fieldValues.serviceName.value
            ) &&
            Number(
              DBParams?.find(
                (params) =>
                  isSelectOption(fieldValues.serviceName) &&
                  params.service_name === fieldValues.serviceName.value
              )?.disk_max
            ),
          defaultValue: (fieldValues: Record<string, unknown>) =>
            DBParams?.find(
              (params) =>
                isSelectOption(fieldValues.serviceName) &&
                params.service_name === fieldValues.serviceName.value
            ) &&
            Number(
              DBParams?.find(
                (params) =>
                  isSelectOption(fieldValues.serviceName) &&
                  params.service_name === fieldValues.serviceName.value
              )?.disk_min
            ),
          rules: diskSizeSchema(DBParams),
          isHidden: (fieldValues) => !fieldValues.serviceName
        },
        {
          name: "ipACL",
          type: FIELD_TYPES.MULTILINE_TEXT,
          label: "IP ACL",
          rules: ipACLSchema
        },
        {
          name: "enable_ilb",
          type: FIELD_TYPES.CHECKBOX,
          label: "Provision in private network"
        },
        {
          name: "network_id",
          type: FIELD_TYPES.SELECT,
          label: "Network",
          options: networks?.map((network) =>
            getSelectOption(network, "name", "id")
          ),
          rules: selectOptionSchema.when(
            "enable_ilb",
            (value: boolean[], schema: Schema<SelectOption>) =>
              value[0] ? schema : mixed().notRequired()
          ),
          isHidden: (fieldValues) => !fieldValues.enable_ilb
        },
        {
          name: "subnet_id",
          type: FIELD_TYPES.SELECT,
          label: "Subnet",
          options: (fieldValues) =>
            subnets
              ?.filter(
                (subnet) =>
                  subnet.network_id ===
                  networks?.find(
                    (network) =>
                      isSelectOption(fieldValues.network_id) &&
                      network.id === fieldValues.network_id.value
                  )?.id
              )
              .map((subnet) => getSelectOption(subnet, "name", "id")),
          rules: selectOptionSchema.when(
            "enable_ilb",
            (value: boolean[], schema: Schema<SelectOption>) =>
              value[0] ? schema : mixed().notRequired()
          ),
          isHidden: (fieldValues) => !fieldValues.network_id
        },
        {
          name: "private_static_ip",
          type: FIELD_TYPES.TEXT,
          rules: string().when(
            "subnet_id",
            (subnet_id: SelectOption[], schema: StringSchema) =>
              subnet_id[0]
                ? schema
                    .matches(REGEX.IP_ADDRESS, {
                      message:
                        'IP must have the valid format (e.g., "192.168.1.101")',
                      excludeEmptyString: true
                    })
                    .test(
                      "privateStaticIpValidFormat",
                      "IP must be in subnet range",
                      (value) => {
                        const cidr = subnets?.find(
                          (subnet) => subnet.id === subnet_id[0]?.value
                        )?.cidr;
                        return Boolean(
                          value && cidr && isIPv4InCIDR(value, cidr)
                        );
                      }
                    )
                : schema
          ),
          isHidden: (fieldValues) => !fieldValues.network_id,
          label: "Private static IP"
        },
        {
          name: "ha_enabled",
          type: FIELD_TYPES.CHECKBOX,
          label: "Enable High Availability"
        }
      ]
    },
    [DIALOG_TYPES.EDIT]: {
      onConfirm: handleConfirmEditDatabase,
      title: "Edit database",
      confirmButtonLabel: "Save",
      fields: [
        {
          name: "version",
          type: FIELD_TYPES.SELECT,
          label: "Version",
          defaultValue: getSelectDefaultValue(
            databases,
            selectedItemId,
            "version",
            "version"
          ),
          options: versionSelectOptions(editedDatabase),
          rules: selectOptionSchema
        },
        {
          name: "port",
          type: FIELD_TYPES.NUMBER,
          label: "Port",
          defaultValue: editedDatabase?.port ?? "",
          min: MIN_PORT,
          max: MAX_PORT,
          rules: (isUndefined(editedDatabase?.port)
            ? number().optional()
            : number().required()
          )
            .transform((value: number, originalValue: unknown) =>
              isString(originalValue) && originalValue.trim() === ""
                ? undefined
                : value
            )
            .integer()
            .min(
              MIN_PORT,
              `Port must be greater than or equals to ${MIN_PORT}.`
            )
            .max(MAX_PORT, `Port must be less than ${MAX_PORT}.`)
        },
        {
          name: "cpu",
          type: FIELD_TYPES.SELECT,
          label: "vCPUs",
          defaultValue: getSelectDefaultValue(
            databases,
            selectedItemId,
            "cpuString",
            "cpu"
          ),
          options: DBParams?.find(
            (params) =>
              params.service_name ===
              databases?.find((database) => database.id === selectedItemId)
                ?.serviceName
          )?.cpu.map((x) => ({
            label: `${Number(x) / 1000} vCPU`,
            value: x
          })),
          rules: selectOptionSchema
        },
        {
          name: "memory",
          type: FIELD_TYPES.SELECT,
          label: "Memory",
          defaultValue: getSelectDefaultValue(
            databases,
            selectedItemId,
            "memoryString",
            "memory"
          ),
          options: DBParams?.find(
            (params) =>
              params.service_name ===
              databases?.find((database) => database.id === selectedItemId)
                ?.serviceName
          )?.memory.map((x) => ({
            label: `${Number(x) / 1024} GiB`,
            value: x
          })),
          rules: selectOptionSchema
        },
        {
          name: "ha_enabled",
          type: FIELD_TYPES.CHECKBOX,
          label: "Enable High Availability",
          defaultValue: Boolean(
            databases?.find((database) => database.id === selectedItemId)
              ?.high_availability?.enabled
          )
        }
      ]
    },
    [DIALOG_TYPES.DELETE]: {
      onConfirm: handleConfirmDeleteDatabase,
      title: `Are you sure you want to delete the "${
        deletingDatabaseName ?? "selected"
      }" database?`,
      fields: [
        {
          name: "confirmationName",
          type: FIELD_TYPES.TEXT,
          label: "Type the database name to confirm deletion",
          rules: string()
            .required()
            .test({
              name: "validateConfirmationName",
              message: "Database name does not match",
              test: function (value) {
                return value === deletingDatabaseName;
              }
            })
        }
      ],
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.RESET_PASSWORD]: {
      onConfirm: handleConfirmDatabasePasswordDialog,
      title: "Are you sure you want to reset password?",
      confirmButtonLabel: "Reset"
    }
  };

  return (
    <>
      <Head title={title} />
      {organization && project && <Breadcrumbs breadcrumbs={breadcrumbs} />}
      <Typography variant={"h4"} component={"h2"}>
        <Badge
          color="warning"
          badgeContent={
            <span style={{ paddingLeft: "5px", paddingRight: "5px" }}>
              beta
            </span>
          }
          style={{ paddingRight: "20px" }}
        >
          {title}
        </Badge>
      </Typography>
      <Table
        isSearchEnabled={true}
        isSortingEnabled={true}
        rows={databases || []}
        columns={tableColumns}
        actions={tableActions}
        itemLink={{
          column: "name",
          getURL: generateTableItemURL
        }}
        // Spinner until the first load only, not on every poll
        isLoading={!databases}
        toolbarItems={
          <Button
            onClick={handleCreateDatabaseButtonClick}
            variant={"contained"}
            disabled={!DBParams}
          >
            Create database
          </Button>
        }
      />
      <FormDialog
        isOpened={dialog.isOpened}
        onCancel={handleCloseDialog}
        fields={dialogProps[dialog.type].fields}
        onConfirm={dialogProps[dialog.type].onConfirm}
        title={dialogProps[dialog.type].title}
        confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
      />
      {database && isString(database.password) && (
        <DatabasePasswordDialog
          isOpened={isDatabasePasswordDialogOpened}
          name={database.name}
          text={selectedDatabasePasswordDialogText}
          password={database.password}
          onClose={handleConfirmDatabasePasswordDialog}
        />
      )}
    </>
  );
};
